import { ADD_CLIENT_FORM_SELECTOR, updateRecipientsLists } from '@/company-portal/add-file';
import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { createEmptyErrors } from '@/components/Views/Company/File/Client/client-info-form.validation';
import { v4 as uuidv4 } from 'uuid';
import { onUnmounted, reactive, ref } from 'vue';
import type { Client, ClientInfoFormRowIndex } from './add-file.types';
import { clientInfoErrors } from './handle-form-errors';

export type ClientInfoFormOptions = {
    minVisibleClientInfoForms?: number;
    hiddenClientInfoFormsCount?: number;
};

export const userAddedClientInfo = reactive(new Map<ClientInfoFormRowIndex, Client>());
export const tpsClientInfo = reactive(new Map<ClientInfoFormRowIndex, Client>()); // TPS clients

export const useClientInfoForms = ({
    minVisibleClientInfoForms = 0,
    hiddenClientInfoFormsCount = 0,
}: ClientInfoFormOptions = {}) => {
    // needed to help generate unique keys so the client form cards remount when Add File form resets
    const clientsSectionRemountKey = ref(uuidv4());

    // starts at 0 unless there are hidden clients from TPS
    // does not decrease when a row is removed
    let clientInfoFormIndex = hiddenClientInfoFormsCount;

    const getNextClientInfoFormIndex = () => `${clientInfoFormIndex++}`;

    const addClient = () => {
        const index = getNextClientInfoFormIndex();
        userAddedClientInfo.set(index, {
            id: index,
            email: '',
            phone: '',
            role: undefined,
            first: '',
            last: '',
        });
        clientInfoErrors.set(index, createEmptyErrors());
    };

    const removeClient = (key: ClientInfoFormRowIndex) => {
        userAddedClientInfo.delete(key);
        requestAnimationFrame(() => {
            updateRecipientsLists(ADD_CLIENT_FORM_SELECTOR);
        });
        clientInfoErrors.delete(key);
    };

    // reset state after form submission
    const resetClientInfoForms = () => {
        clientInfoFormIndex = hiddenClientInfoFormsCount;
        userAddedClientInfo.clear();
        clientsSectionRemountKey.value = uuidv4();
        while (userAddedClientInfo.size < minVisibleClientInfoForms) {
            addClient();
        }
        updateRecipientsLists(ADD_CLIENT_FORM_SELECTOR);
    };

    resetClientInfoForms();

    const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(resetClientInfoForms);

    onUnmounted(unregisterAddFileSubmittedAction);

    return {
        addClient,
        clientsSectionRemountKey,
        removeClient,
    };
};
